import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { MIGRATION_BACKUP_MAX_AGE_MINUTES, isTerminalJobState, type BackupDTO } from '@devrobot/shared';
import { Link } from 'react-router';
import { CircleCheck, DatabaseBackup, Download, RotateCcw, ShieldCheck } from 'lucide-react';
import { api, seg } from '../../../api/client';
import { createBackup, createdBackupId, qk, useApiMutation, useBackups, useJob } from '../../../api/queries';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { RelativeTime } from '../../../components/RelativeTime';
import { SkeletonLines } from '../../../components/Skeleton';
import { StatusBadge } from '../../../components/StatusBadge';
import { Haptic } from '../../../kit/haptics';
import { haptic } from '../../../kit/native';
import { formatBytes } from '../../../lib/format';
import { backupKey, isUsableBackup, type RequiredBackup, type StepProps, type WizardState } from './model';

const newestFirst = (a: BackupDTO, b: BackupDTO) => Date.parse(b.createdAt) - Date.parse(a.createdAt);

/**
 * Whether every required backup has a chosen, verified and fresh backup. Shares the backups
 * query cache with the rows, so the wizard's Next button follows them.
 */
export function useSafetyStatus(required: RequiredBackup[], state: WizardState, acknowledgementIds: string[]) {
  const targetIds = [...new Set(required.map((r) => r.targetId))];
  const results = useQueries({
    queries: targetIds.map((id) => ({ queryKey: qk.backups(id), queryFn: () => api.get<BackupDTO[]>(`/api/v1/targets/${seg(id)}/backups`) })),
  });
  const byTarget = new Map(targetIds.map((id, i) => [id, results[i]?.data ?? []]));
  const missingBackups = required.filter((r) => {
    const chosen = state.backups[backupKey(r)];
    return !isUsableBackup(byTarget.get(r.targetId)?.find((b) => b.id === chosen), r.database);
  });
  const missingAcks = acknowledgementIds.filter((id) => !state.acknowledgements.includes(id));
  const chosenIds = (side: RequiredBackup['side']) => required.filter((r) => r.side === side).map((r) => state.backups[backupKey(r)]).filter((id): id is string => Boolean(id));
  return { ok: missingBackups.length === 0 && missingAcks.length === 0, missingBackups, missingAcks, sourceBackupIds: chosenIds('source'), destBackupIds: chosenIds('destination') };
}

function BackupFacts({ b }: { b: BackupDTO }) {
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-base-content/65">
      <StatusBadge kind="backup" value={b.state} size="xs" />
      <span>
        taken <RelativeTime value={b.createdAt} />
      </span>
      <span className="tabular-nums">{formatBytes(b.sizeBytes)}</span>
      {b.sha256 ? (
        <span className="font-mono" title={b.sha256}>
          sha256 {b.sha256.slice(0, 12)}…
        </span>
      ) : null}
      {b.purpose === 'pre-migration' ? <span className="badge badge-ghost badge-xs">pre-migration</span> : null}
    </span>
  );
}

export function RequiredBackupRow({ req, chosenId, onChoose }: { req: RequiredBackup; chosenId: string | undefined; onChoose: (id: string | null) => void }) {
  const [started, setStarted] = useState<{ jobId: string; backupId?: string } | null>(null);
  const job = useJob(started?.jobId ?? '', { enabled: Boolean(started), poll: true });
  const jobDone = Boolean(job.data && isTerminalJobState(job.data.state));
  const backups = useBackups(req.targetId, { refetchInterval: started && !jobDone ? 3000 : false });
  const create = useApiMutation(() => createBackup(req.targetId, { database: req.database, purpose: 'pre-migration' }), { invalidate: [qk.backups(req.targetId), ['jobs']] });

  const list = (backups.data ?? []).filter((b) => b.database.service === req.database).sort(newestFirst);
  const chosen = list.find((b) => b.id === chosenId);
  const usable = isUsableBackup(chosen, req.database);
  const latestUsable = list.find((b) => isUsableBackup(b, req.database));
  const startedBackup = started ? list.find((b) => (started.backupId ? b.id === started.backupId : b.jobId === started.jobId)) : undefined;

  // When the create response carried no backup id, adopt the backup the job produced.
  useEffect(() => {
    if (startedBackup && startedBackup.id !== chosenId) onChoose(startedBackup.id);
  }, [startedBackup?.id]);

  const start = () =>
    create.mutate(undefined, {
      onSuccess: (r) => {
        const id = createdBackupId(r);
        setStarted({ jobId: r.job.id, backupId: id });
        if (id) onChoose(id);
      },
      onError: () => haptic('error'),
    });

  const inFlight = Boolean(started && !jobDone && !(startedBackup && ['verified', 'failed'].includes(startedBackup.state)));
  const failed = Boolean((startedBackup && startedBackup.state === 'failed') || (job.data && ['failed', 'canceled'].includes(job.data.state) && !usable));

  return (
    <li className={`space-y-2 rounded-field border p-4 ${usable ? 'border-success/40' : 'border-hairline'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2 text-sm">
          {usable ? <CircleCheck className="size-4 dr-ink-success" aria-hidden="true" /> : <DatabaseBackup className="size-4 text-base-content/55" aria-hidden="true" />}
          <span className="font-mono font-medium">{req.database}</span>
          <span className={`badge badge-xs ${req.side === 'source' ? 'badge-soft badge-info' : 'badge-soft badge-warning'}`}>{req.side}</span>
          {usable ? <span className="dr-ink-success">ready</span> : null}
        </span>
        <Link className="link text-xs" to={`/targets/${req.targetId}?tab=backups`}>
          Backups of this target
        </Link>
      </div>
      <p className="text-sm text-base-content/70">{req.reason}</p>
      {backups.isPending ? <SkeletonLines lines={1} /> : null}
      <ErrorAlert error={backups.error} onRetry={() => void backups.refetch()} />

      {chosen ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-field bg-base-200/60 p-2.5">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium">{usable ? 'Using this backup' : chosen.state === 'verified' ? 'This backup is too old' : 'Selected backup'}</p>
            <BackupFacts b={chosen} />
            {chosen.error ? <p className="text-xs break-anywhere dr-ink-error">{chosen.error}</p> : null}
          </div>
          {usable ? (
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => onChoose(null)}>
              Change
            </button>
          ) : null}
        </div>
      ) : null}

      {started && !startedBackup && job.data ? (
        <p className="flex flex-wrap items-center gap-2 text-xs text-base-content/65">
          Backup job <StatusBadge kind="job" value={job.data.state} size="xs" />
          {job.data.stage && !jobDone ? <span>{job.data.stage}</span> : null}
          <Link className="link" to={`/jobs/${started.jobId}`}>
            View job
          </Link>
        </p>
      ) : null}
      {inFlight ? (
        <p className="flex items-center gap-2 text-xs text-base-content/65" role="status">
          <span className="loading loading-spinner loading-xs" aria-hidden="true" /> Backing up and verifying… this step is ready once the backup is verified.
        </p>
      ) : null}
      {failed && job.data?.result.message ? <p className="text-xs break-anywhere dr-ink-error">{job.data.result.message}</p> : null}
      <ErrorAlert error={create.error} title="The backup was not started" />

      {!usable ? (
        <div className="flex flex-wrap items-center gap-2">
          {latestUsable && latestUsable.id !== chosenId ? (
            <div className="flex w-full flex-wrap items-center justify-between gap-2 rounded-field bg-base-200/60 p-2.5">
              <div className="min-w-0 space-y-1">
                <p className="text-xs font-medium">Recent verified backup available</p>
                <BackupFacts b={latestUsable} />
              </div>
              <button type="button" className="btn btn-soft btn-secondary btn-xs" onClick={() => onChoose(latestUsable.id)}>
                Use this backup
              </button>
            </div>
          ) : null}
          <Haptic disabled={create.isPending || inFlight}>
            <button type="button" className="btn btn-sm" disabled={create.isPending || inFlight} onClick={start}>
              {create.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : failed ? <RotateCcw className="size-4" aria-hidden="true" /> : <DatabaseBackup className="size-4" aria-hidden="true" />}
              {failed ? 'Retry backup' : 'Create backup now'}
            </button>
          </Haptic>
        </div>
      ) : null}
    </li>
  );
}

export function SafetyStep({ state, dispatch, preview }: StepProps) {
  const plan = preview.plan;
  if (!plan) return <SkeletonLines lines={6} />;
  return (
    <div className="space-y-6">
      <div role="note" className="alert alert-info alert-soft items-start text-sm">
        <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          <p className="font-semibold">Keep your own copy</p>
          <p>
            DevRobot requires a verified database backup taken within the last {MIGRATION_BACKUP_MAX_AGE_MINUTES} minutes. It does not back up file volumes: if they matter, make and keep an external copy (for example a download of the volume) before you continue.
          </p>
        </div>
      </div>

      <section>
        <h3 className="mb-2 flex items-center gap-2 font-semibold">
          <Download className="size-4" aria-hidden="true" /> Required backups
        </h3>
        {plan.requiredBackups.length === 0 ? <p className="text-sm text-base-content/60">No database backup is required for this selection.</p> : null}
        <ul className="space-y-3">
          {plan.requiredBackups.map((r) => {
            const key = backupKey(r);
            return <RequiredBackupRow key={key} req={r} chosenId={state.backups[key]} onChoose={(id) => dispatch({ type: 'choose-backup', key, backupId: id })} />;
          })}
        </ul>
      </section>

      {plan.requiredAcknowledgements.length > 0 ? (
        <fieldset>
          <legend className="mb-2 font-semibold">Confirm you understand</legend>
          <ul className="space-y-2">
            {plan.requiredAcknowledgements.map((a) => (
              <li key={a.id}>
                <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm mt-0.5"
                    checked={state.acknowledgements.includes(a.id)}
                    onChange={(e) => dispatch({ type: 'toggle', field: 'acknowledgements', value: a.id, on: e.target.checked })}
                  />
                  <span>{a.text}</span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      ) : null}
    </div>
  );
}
